import { useQuery } from "@tanstack/react-query"
import bulkBackgroundChecksQuery from "./services/bulk-background-checks"

import { Download } from "lucide-react"

const statusStyles = {
    init: "bg-gray-100 text-gray-700",
    completed: "bg-green-100 text-green-700",
}

const BulkUploads = () => {
    const {
        data = [],
        error,
        isFetching,
    } = useQuery({
        queryKey: ["bulk-background-checks"],
        queryFn: bulkBackgroundChecksQuery,
        refetchOnWindowFocus: false,
    })

    if (isFetching) {
        return (
            <div className="p-6 space-y-4">
                <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
                <div className="h-40 bg-gray-100 rounded animate-pulse" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                    Failed to load bulk uploads.
                </div>
            </div>
        )
    }

    if (!data.length) {
        return (
            <div className="p-6 text-center">
                <h2 className="text-lg font-semibold">No Bulk Uploads</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Bulk background check uploads will appear here.
                </p>
            </div>
        )
    }

    return (
        <div className="p-6">
            <h1 className="text-xl font-semibold mb-4">Bulk Uploads</h1>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Bulk ID</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Progress</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Created</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Completed</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">File</th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 bg-white">
                        {data.map((bulk) => {
                            const progress =
                                bulk.itemCount > 0
                                    ? Math.round(
                                        ((bulk.completedItemCount || 0) / bulk.itemCount) * 100
                                    )
                                    : 0

                            return (
                                <tr key={bulk._id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-mono text-xs">
                                        {bulk._id}
                                    </td>

                                    <td className="px-4 py-3">
                                        <span
                                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[bulk.status] ??
                                                "bg-gray-100 text-gray-700"
                                                }`}
                                        >
                                            {bulk.status}
                                        </span>
                                    </td>

                                    <td className="px-4 py-3">
                                        <div className="w-40">
                                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                                <span>
                                                    {bulk.completedItemCount || 0}/{bulk.itemCount}
                                                </span>
                                                <span>{progress}%</span>
                                            </div>
                                            <div className="h-2 w-full rounded bg-gray-200">
                                                <div
                                                    className="h-2 rounded bg-blue-600 transition-all"
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                        </div>
                                    </td>

                                    <td className="px-4 py-3 text-gray-600">
                                        {new Date(bulk.createdAt).toLocaleString()}
                                    </td>

                                    <td className="px-4 py-3 text-gray-600">
                                        {bulk.completedAt
                                            ? new Date(bulk.createdAt).toLocaleString()
                                            : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        {bulk.fileKey ? (
                                            <a
                                                href={bulk.fileKey}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                                                title="Open file in new tab"
                                            >
                                                <Download size={16} />
                                            </a>
                                        ) : (
                                            <span className="text-gray-400 text-xs">—</span>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default BulkUploads
